import { Box, Typography } from '@mui/material';
import { formatTranslation } from 'intl';
import { InspectionTranslationKeys, FormTranslationKeys } from 'intl/keys';
import { ReactElement, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { MemoStatefulRichTextField } from 'components/dataCreationForms/withInputForm/statefulInputsWrapper';
import { CollapsibleInspectionComment } from 'components/inspection/CollapsibleInspectionComment';
import { InspectionCommentLabelVariant } from 'components/inspection/CollapsibleInspectionComment/types';
import {
  InputFormGraphicUploader,
  GraphicUploader,
} from 'components/common/graphicUploader';
import { GU } from 'components/common/graphicUploader/types';
import { useDisplayedFilesStoreHasFiles } from 'components/common/gallery/filesStore';
import { RichTextView } from 'components/common/RichTextEditor/RichTextView';
import { fieldLabelSx } from 'shared/styles/typography';
import {
  protocolItemSectionSx,
  protocolItemSectionsStackSx,
} from 'components/inspection/ProtocolItemIssues/styles';

interface ChecklistSectionCommentBlockProps {
  description?: string;
  comment?: string;
  editable: boolean;
  formKey?: string;
  fieldName?: string;
  templateGraphicUploader?: GU;
  graphicUploader?: GU;
  uploaderKey?: string;
  forceShowTemplateGraphicUploader?: boolean;
  forceShowProtocolGraphicUploader?: boolean;
}

export function ChecklistSectionCommentBlock({
  description,
  comment,
  editable,
  formKey,
  fieldName,
  templateGraphicUploader,
  graphicUploader,
  uploaderKey,
  forceShowTemplateGraphicUploader = false,
  forceShowProtocolGraphicUploader = false,
}: ChecklistSectionCommentBlockProps): ReactElement {
  const intl = useIntl();
  const hasTemplateFiles = useDisplayedFilesStoreHasFiles(
    templateGraphicUploader?.filesStore
  );
  const hasProtocolFiles = useDisplayedFilesStoreHasFiles(
    graphicUploader?.filesStore
  );
  const shouldShowTemplateUploader =
    hasTemplateFiles || forceShowTemplateGraphicUploader;
  // Editable sections always offer the add tile (as on issues).
  const showProtocolUploader =
    editable || hasProtocolFiles || forceShowProtocolGraphicUploader;

  const commentFieldProps = useMemo(() => ({
    formKey: formKey || '',
    labelId: InspectionTranslationKeys.comment,
    fieldName: fieldName || '',
    defaultValue: comment || '',
    disabled: false,
    issueParams: null,
    multiline: true,
    minRows: 3,
    reserveSpaceForHelperText: true,
    dense: true,
    placeholder: formatTranslation(
      intl,
      InspectionTranslationKeys.commentAddPlaceholder
    ),
  }), [comment, fieldName, formKey, intl]);

  const commentLabel = formatTranslation(intl, InspectionTranslationKeys.comment);

  return (
    <Box sx={protocolItemSectionsStackSx}>
      {description ? (
        <Box sx={protocolItemSectionSx}>
          <Typography component='p' sx={fieldLabelSx}>
            {formatTranslation(intl, InspectionTranslationKeys.itemPreviewFieldLabelDescription)}
          </Typography>
          <RichTextView content={description} />
        </Box>
      ) : null}

      {shouldShowTemplateUploader && templateGraphicUploader ? (
        <Box sx={protocolItemSectionSx}>
          <Typography component='p' sx={fieldLabelSx}>
            {formatTranslation(
              intl,
              InspectionTranslationKeys.sectionPreviewFieldLabelTemplateAttachments
            )}
          </Typography>
          <GraphicUploader {...templateGraphicUploader} />
        </Box>
      ) : null}

      {editable ? (
        <>
          <Box sx={protocolItemSectionSx}>
            <Typography component='p' sx={fieldLabelSx}>
              {commentLabel}
            </Typography>
            <CollapsibleInspectionComment
              formKey={commentFieldProps.formKey}
              fallbackValue={comment || ''}
              labelVariant={InspectionCommentLabelVariant.section}
            >
              <MemoStatefulRichTextField {...commentFieldProps} />
            </CollapsibleInspectionComment>
          </Box>
          {showProtocolUploader && graphicUploader && uploaderKey ? (
            <Box sx={protocolItemSectionSx}>
              <Typography component='p' sx={fieldLabelSx}>
                {formatTranslation(
                  intl,
                  FormTranslationKeys.sectionProblemRelatedAttachments
                )}
              </Typography>
              <InputFormGraphicUploader
                uploaderKey={uploaderKey}
                GU={graphicUploader}
              />
            </Box>
          ) : null}
        </>
      ) : (
        <>
          {comment ? (
            <Box sx={protocolItemSectionSx}>
              <Typography component='p' sx={fieldLabelSx}>
                {commentLabel}
              </Typography>
              <RichTextView content={comment} />
            </Box>
          ) : null}
          {graphicUploader && showProtocolUploader ? (
            <Box sx={protocolItemSectionSx}>
              <Typography component='p' sx={fieldLabelSx}>
                {formatTranslation(
                  intl,
                  FormTranslationKeys.sectionProblemRelatedAttachments
                )}
              </Typography>
              <GraphicUploader {...graphicUploader} />
            </Box>
          ) : null}
        </>
      )}
    </Box>
  );
}
